import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createMachineOption,
  deleteMachineOption,
  getMachineOption,
  listMachineNames,
  listMachineOptions,
  updateMachineOption,
  type CreateMachineOptionBody,
  type ListMachineOptionsParams,
  type MachineNameOption,
  type MachineOption,
  type PaginatedMachineOptions,
  type UpdateMachineOptionBody,
} from "../api/machineOptions";

/** Paginated machine-options list (§L — HTTP via TanStack Query). */
export function useMachineOptions(params: ListMachineOptionsParams) {
  return useQuery<PaginatedMachineOptions>({
    queryKey: ["machine-options", "list", params],
    queryFn: () => listMachineOptions(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Single machine option for the edit page prefill. */
export function useMachineOption(partNo: string | null, regionId = 1) {
  return useQuery<MachineOption>({
    queryKey: ["machine-options", "detail", regionId, partNo],
    queryFn: () => getMachineOption(partNo as string, regionId),
    enabled: !!partNo,
    staleTime: 30_000,
  });
}

/** Selectable machine names for the Available On picker (rarely changes). */
export function useMachineNames() {
  return useQuery<MachineNameOption[]>({
    queryKey: ["machine-options", "machine-names"],
    queryFn: listMachineNames,
    staleTime: 5 * 60 * 1000,
  });
}

/** POST /v1/machine-options; refreshes the list. */
export function useCreateMachineOption() {
  const queryClient = useQueryClient();
  return useMutation<MachineOption, Error, CreateMachineOptionBody>({
    mutationFn: (body) => createMachineOption(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["machine-options"] });
    },
  });
}

/** PATCH /v1/machine-options/:partNo; refreshes the list + edited detail. */
export function useUpdateMachineOption() {
  const queryClient = useQueryClient();
  return useMutation<MachineOption, Error, { partNo: string; body: UpdateMachineOptionBody }>({
    mutationFn: ({ partNo, body }) => updateMachineOption(partNo, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["machine-options"] });
    },
  });
}

/** Soft-delete via DELETE /v1/machine-options/:partNo?regionId=. */
export function useDeleteMachineOption() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, { partNo: string; regionId?: number }>({
    mutationFn: ({ partNo, regionId }) => deleteMachineOption(partNo, regionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["machine-options"] });
    },
  });
}
